import { useEffect, useState } from "react";
import { Radio, Volume2, VolumeX, Activity, Zap, LogOut, User, PlugZap, Lock, Unlock, PanelsTopLeft } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { PANE_TITLES } from "./DashboardGrid";
import { IDS } from "../constants/testIds";
import { simStatus, simToggle, zelloStatus } from "../lib/api";
import { fmtTime, tzAbbrev } from "../lib/time";
import SettingsDialog from "./SettingsDialog";
import { useAuth } from "../context/AuthContext";

const ZELLO_PILL = {
  connected: "bg-emerald-50 border-emerald-200 text-emerald-700",
  connecting: "bg-amber-50 border-amber-200 text-amber-700",
  error: "bg-red-50 border-red-200 text-red-700",
  disconnected: "bg-slate-100 border-slate-200 text-slate-600",
};

export default function DispatchHeader({ muted, onMuteToggle, timezone = "UTC", settings, onSaveSettings, onResetLayout, layoutLocked, onToggleLayoutLock, speechSupported, hiddenPanes = [], onTogglePane }) {
  const { user, logout } = useAuth();
  const [clock, setClock] = useState("");
  const [simOn, setSimOn] = useState(true);
  const [zello, setZello] = useState({ status: "disconnected" });

  useEffect(() => {
    const t = setInterval(() => setClock(`${fmtTime(new Date().toISOString(), timezone)} ${tzAbbrev(timezone)}`), 1000);
    simStatus().then(s => setSimOn(s.running)).catch(() => {});
    return () => clearInterval(t);
  }, [timezone]);

  useEffect(() => {
    const poll = () => zelloStatus().then(setZello).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  const handleSimToggle = async () => { const s = await simToggle(); setSimOn(s.running); };
  const live = zello.status === "connected";

  return (
    <header data-testid={IDS.header.root} className="flex flex-wrap items-center justify-between gap-4 px-6 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-40">
      <div className="flex items-center gap-4">
        <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center shadow-md"><Radio className="h-5 w-5" /></div>
        <div className="leading-tight">
          <div className="overline">Zello · Dispatch Console</div>
          <div className="text-lg font-semibold tracking-tight">Operations Command</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div data-testid={IDS.zello.headerStatus} title={zello.error || ""} className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border ${ZELLO_PILL[zello.status] || ZELLO_PILL.disconnected}`}>
          <PlugZap className="h-3.5 w-3.5" />
          <span className="text-xs font-medium tracking-wide">{live ? "ZELLO LIVE" : zello.status === "connecting" ? "ZELLO CONNECTING" : zello.status === "error" ? "ZELLO ERROR" : "SIMULATOR"}</span>
        </div>
        <div data-testid={IDS.header.connectionStatus} className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
          <span className="dot online" /><span className="text-xs font-medium tracking-wide">CONNECTED</span>
        </div>
        <div data-testid={IDS.header.clock} className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200 font-mono text-xs text-slate-700">
          <Activity className="h-3.5 w-3.5" />{clock}
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-slate-100 border border-slate-200">
          <Zap className="h-3.5 w-3.5 text-amber-500" /><span className="text-xs font-medium text-slate-700">Sim</span>
          <Switch data-testid={IDS.header.simToggle} checked={simOn} onCheckedChange={handleSimToggle} />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={IDS.layout.menu} variant="outline" size="sm" className="gap-1.5" title="Show / hide panes">
              <PanelsTopLeft className="h-4 w-4" /><span className="hidden xl:inline">Panes</span>
              {hiddenPanes.length > 0 && <span className="text-[10px] font-mono px-1 rounded bg-amber-100 text-amber-700">{hiddenPanes.length} hidden</span>}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs">Show panes</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.entries(PANE_TITLES).map(([id, title]) => (
              <DropdownMenuCheckboxItem key={id} data-testid={IDS.layout.menuItem(id)} checked={!hiddenPanes.includes(id)} onCheckedChange={() => onTogglePane(id)} onSelect={e => e.preventDefault()}>
                {title}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button data-testid={IDS.layout.lock} variant="outline" size="sm" onClick={onToggleLayoutLock} className="gap-1.5" title={layoutLocked ? "Unlock layout to drag & resize panes" : "Lock layout"}>
          {layoutLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4 text-amber-600" />}
          <span className="hidden xl:inline">{layoutLocked ? "Layout" : "Editing"}</span>
        </Button>
        <Button data-testid={IDS.header.masterMute} variant={muted ? "destructive" : "outline"} size="sm" onClick={onMuteToggle} className="gap-1.5">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}<span className="hidden xl:inline">{muted ? "Muted" : "Audio"}</span>
        </Button>
        <SettingsDialog settings={settings} onSave={onSaveSettings} onResetLayout={onResetLayout} speechSupported={speechSupported} />
        <div data-testid={IDS.header.userMenu} className="flex items-center gap-2 pl-2 border-l border-slate-200">
          <div className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center"><User className="h-4 w-4" /></div>
          <div className="hidden md:block leading-tight">
            <div className="text-xs font-semibold truncate max-w-[140px]">{user?.name || user?.email}</div>
            <div className="text-[10px] text-slate-500 uppercase font-mono">{user?.role}</div>
          </div>
          <Button data-testid={IDS.header.logout} variant="ghost" size="sm" onClick={logout} aria-label="Sign out"><LogOut className="h-4 w-4" /></Button>
        </div>
      </div>
    </header>
  );
}
